/**
 * RPC client for the telemetry read backend.
 * All endpoints are POST /rpc/{Method} with JSON request/response bodies.
 */

/** Custom error class that carries the HTTP status code. */
export class RpcError extends Error {
  public readonly status: number;

  constructor(status: number, method: string, detail: string) {
    super(`RPC ${method} failed (${status}): ${detail}`);
    this.name = 'RpcError';
    this.status = status;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

let authToken: string | null = typeof localStorage !== 'undefined' ? localStorage.getItem('auth-token') : null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function getAuthToken(): string | null {
  return authToken;
}

export function getApiBaseUrl(): string {
  const envUrl = (import.meta as any).env?.VITE_API_BASE_URL;
  if (envUrl) return envUrl;
  const winConfig = typeof window !== 'undefined' ? (window as any).__CONFIG__?.API_BASE_URL : '';
  if (winConfig) return winConfig;
  const isNative = typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor?.isNativePlatform?.();
  if (isNative) {
    return 'https://kart.win';
  }
  return '';
}

/** Silently file a bug report for an RPC error (fire-and-forget, never blocks or throws). */
function autoReportBug(method: string, status: number, detail: string): void {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    const baseUrl = getApiBaseUrl();
    void fetch(`${baseUrl}/rpc/CreateBugReport`, {
      method: 'POST',
      headers,
      keepalive: true,
      body: JSON.stringify({
        description: `[Auto-reported] RPC ${method} failed (${status}): ${detail}`,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        appLanguage: document.documentElement.lang || '',
      }),
    }).catch(() => {
      // Best-effort — silently ignore failures
    });
    window.dispatchEvent(new CustomEvent('bug:auto-reported'));
  } catch {
    // Best-effort — silently ignore failures
  }
}

export async function rpc<T = unknown>(method: string, body: Record<string, unknown> = {}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const baseUrl = getApiBaseUrl();
  let res: Response;
  try {
    res = await fetch(`${baseUrl}/rpc/${method}`, {
      method: 'POST',
      headers,
      cache: 'no-store',
      body: JSON.stringify(body),
    });
  } catch (err) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('network:offline'));
    }
    throw err;
  }

  if (!res.ok) {
    if (res.status === 401) {
      window.dispatchEvent(new CustomEvent('auth:signout'));
    }
    const text = await res.text().catch(() => '');
    const isTermsRequired = res.status === 403 && text.includes('TERMS_NOT_ACCEPTED');
    if (isTermsRequired) {
      window.dispatchEvent(new CustomEvent('auth:terms-required'));
    }
    // Auto-report backend errors (skip 401/auth, terms required, and CreateBugReport to avoid recursion)
    if (res.status !== 401 && !isTermsRequired && method !== 'CreateBugReport') {
      autoReportBug(method, res.status, text || res.statusText);
    }
    throw new RpcError(res.status, method, text || res.statusText);
  }
  return res.json();
}

/** Generate a UUID v4 (client side). */
export function uuid(): string {
  return crypto.randomUUID();
}

export interface ShareUser {
  id: string;
  name: string;
}

export interface Share {
  id: string;
  ownerId: string;
  teamId: string;
  shareAll: boolean;
  sessionIds: string[];
  createdAt: string;
  owner: ShareUser;
  team: { id: string; name: string; ownerId: string };
}

export async function listMyShares(
  outgoingPage: number = 1,
  incomingPage: number = 1,
  pageSize: number = 10
): Promise<{
  outgoing: Share[];
  outgoingTotalCount: number;
  incoming: Share[];
  incomingTotalCount: number;
  outgoingPage: number;
  incomingPage: number;
  pageSize: number;
}> {
  return rpc('ListMyShares', { outgoingPage, incomingPage, pageSize });
}

export async function listTeamShares(
  teamId: string,
  page: number = 1,
  pageSize: number = 10,
  query: string = ''
): Promise<{
  shares: Share[];
  totalCount: number;
  page: number;
  pageSize: number;
}> {
  return rpc('ListTeamShares', { teamId, page, pageSize, query });
}

export async function shareSessions(id: string, teamId: string, shareAll: boolean, sessionIds: string[]): Promise<void> {
  return rpc('ShareSessions', { id, teamId, shareAll, sessionIds });
}

export async function unshareSession(shareId: string): Promise<void> {
  return rpc('UnshareSession', { shareId });
}

export async function searchUsers(query: string): Promise<{users: ShareUser[]}> {
  return rpc('SearchUsers', { query });
}
